import { StatusBadge, BOOKING_STATUS_META, fmtDate } from "../../lib/status";
import { ConflictBadge, BufferWarningBadge } from "./ConflictBadge";

/** Mobile default: a compact, vertically-scrolling day list instead of a
 * shrunk-down desktop timeline: each vehicle is a tappable card showing
 * today's bookings/status at a glance. */
export function MobileDayView({ vehicles, onOpenBooking, onOpenIssue, onOpenVehicle, onEmptySlot, day }) {
  return (
    <div className="space-y-2" data-testid="fc-mobile-day-view">
      {vehicles.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="fc-mobile-empty">
          No vehicles match the current filters.
        </div>
      )}
      {vehicles.map((v) => {
        const bookingBlocks = (v.blocks || []).filter((b) => b.type === "booking");
        const statusBlock = (v.blocks || []).find((b) => b.type === "vehicle_status");
        const hasIssues = v.state_conflicts?.length > 0 || v.overlap_conflicts?.length > 0 || v.buffer_warnings?.length > 0;
        return (
          <div key={v.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`fc-mobile-card-${v.id}`}>
            <button type="button" onClick={() => onOpenVehicle(v.id)} data-testid={`fc-mobile-card-header-${v.id}`}
                    className="w-full text-left px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-slate-50">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{v.name}</div>
                <div className="text-[11px] text-slate-500 font-mono">{v.plate_number} · {v.category}</div>
              </div>
              <StatusBadge status={v.status} type="vehicle" />
            </button>

            <div className="px-3 pb-3 space-y-1.5">
              {statusBlock && (
                <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5" data-testid={`fc-mobile-status-${v.id}`}>
                  {statusBlock.label}: since {fmtDate(statusBlock.start)}
                </div>
              )}

              {bookingBlocks.length === 0 && !statusBlock && (
                <button type="button"
                        onClick={() => onEmptySlot(v.id, day, new Date(day.getTime() + 48 * 3600 * 1000))}
                        data-testid={`fc-mobile-empty-slot-${v.id}`}
                        className="w-full text-left text-xs text-slate-500 border border-dashed border-slate-300 rounded-lg px-2.5 py-2 hover:bg-slate-50">
                  No bookings today: tap to create one
                </button>
              )}

              {bookingBlocks.map((b) => {
                const meta = BOOKING_STATUS_META[b.subtype] || BOOKING_STATUS_META.draft;
                return (
                  <button key={b.id} type="button" onClick={() => onOpenBooking(b.id)} data-testid={`fc-mobile-booking-${b.id}`}
                          className={`w-full text-left rounded-lg px-2.5 py-1.5 border ${meta.bg} ${meta.text} ${meta.border}`}>
                    <div className="text-xs font-semibold truncate">{b.customer_name}</div>
                    <div className="text-[11px] opacity-80">{fmtDate(b.start)} → {fmtDate(b.end)} · {meta.label}</div>
                  </button>
                );
              })}

              {hasIssues && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {v.state_conflicts?.length > 0 && (
                    <ConflictBadge kind="state" count={v.state_conflicts.length}
                                   onClick={() => onOpenIssue(v.state_conflicts[0].booking_id)}
                                   testId={`fc-mobile-conflict-state-${v.id}`} />
                  )}
                  {v.overlap_conflicts?.map((c, i) => (
                    <ConflictBadge key={`ov-${i}`} kind="overlap" count={1}
                                   onClick={() => onOpenIssue(c.booking_a.id)}
                                   testId={`fc-mobile-conflict-overlap-${v.id}-${i}`} />
                  ))}
                  {v.buffer_warnings?.map((w, i) => (
                    <BufferWarningBadge key={`bw-${i}`} warning={w}
                                         onClick={() => onOpenIssue(w.booking_a.id)}
                                         testId={`fc-mobile-buffer-${v.id}-${i}`} />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
